import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
import { capitalize } from 'lodash';
import IntercityTripForm from 'components/forms/IntercityTripForm';
import { getClientList, getLocations } from 'store/clientSlice';
import { addUser, getPincodeList } from 'store/pageSlice';
import { getSignedUrl } from 'store/commonSlice';
import { search_type, getuniqueArray } from 'utils';
import { useAuth } from 'hooks';
import { getServerFormatedDate, getServerFormatedDateV2 } from 'utils/helpers';
import { getUIVersion } from 'utils/router-static-data';

export default function CreateIntercityTrip() {
	const { permissions } = useAuth();
	const [formData, setFormData] = useState({});
	const [trip_reason, setTripReason] = useState([]);
	const { cities = [] } = useSelector((state) => state.clientState);
	const dispatch = useDispatch();
	const { id } = useParams();
	const isEdit = !!id;
	const [projectData, setProjects] = useState([]);
	const [vendorsData, setVendordata] = useState([]);
	const [VehicleTypes, setVehicleTypes] = useState([]);
	const [ProjectAdhocFlag, SetProjectAdhocFlag] = useState(false);
	const [sourceCity, setSourceCity] = useState('');
	const [destCity, setDestCity] = useState('');
	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState('');
	const [showLRPrompt, setShowLRPrompt] = useState(false);
	const [pendingValues, setPendingValues] = useState(null);
	const [modalLoading, setModalLoading] = useState(false);
	const [loading, setLoading] = useState(false);
	const formRef = useRef();
	const navigate = useNavigate();
	const location = useLocation();
	const [isvendorBilling, setIsvendorBilling] = React.useState(false);
	const vendorUserType = [
		{ value: 'regular', label: 'Regular' },
		{ value: 'temporary', label: 'Temporary' },
	];

	// Function to load dependent data based on form values
	const loadDependentData = React.useCallback(
		async (formValues) => {
			if (!formValues) return;

			// Load project data for the city - wait for it to complete
			if (formValues.city) {
				await dispatch(
					getClientList({
						search_type: 'project',
						filter_by: {
							city: formValues.city,
							is_active: true,
						},
					})
				).then((projectRes) => {
					const arr = Array.isArray(projectRes?.data)
						? projectRes?.data
						: Object.values(projectRes?.data || {});
					const prodata = arr.map((item) => ({
						label: capitalize(item?.project_location_code),
						value: item?.id,
					}));
					setProjects(prodata);
				});
			}

			// Load vendor data for regular trips
			if (formValues.trip_type === 'regular' && formValues.project_id) {
				dispatch(
					getClientList({
						search_type: 'vendor_contract',
						filter_by: {
							project_id: formValues.project_id,
							city: formValues.city,
							is_active: true,
						},
					})
				).then((vendorRes) => {
					const arr = Array.isArray(vendorRes?.data)
						? vendorRes?.data
						: Object.values(vendorRes?.data || {});
					const data_vendor = arr.map((item) => ({
						value: item?.vendor_id,
						label: item?.vendor_name,
					}));
					setVendordata(data_vendor);
				});
			}

			// Load vendor data for adhoc trips
			if (formValues.trip_type === 'adhoc') {
				getVendorDataList(formValues.vendor_type);
			}

			// Load vehicle types
			getVehicleTypes();
		},
		[dispatch]
	);

	// Check for restored form data from create-lr or adhoc-request and load dependent data
	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (
			location.state?.formData &&
			(location.state?.source === 'create-lr' || location.state?.source === 'adhoc-request')
		) {
			const restoredFormData = location.state.formData;

			// If we have a project_id, fetch that specific project to get its name
			if (restoredFormData.project_id) {
				dispatch(
					getClientList({
						search_type: 'project',
						filter_by: {
							id: restoredFormData.project_id,
						},
					})
				).then((projectRes) => {
					const projectItem = projectRes?.data?.[0];
					if (projectItem) {
						setProjects([
							{
								value: projectItem.id,
								label: capitalize(projectItem.project_location_code),
							},
						]);
					}
				});
			}

			// Load dependent data first, then set form data
			loadDependentData(restoredFormData).then(() => {
				// Update form data with restored values after dependent data is loaded
				setFormData((prevData) => ({
					...prevData,
					...restoredFormData,
				}));
			});

			// Fetch city and state information for pincodes when coming from adhoc-request
			if (location.state?.source === 'adhoc-request') {
				// Fetch city/state for source pincode
				if (restoredFormData.start_location_pincode) {
					dispatch(getPincodeList(restoredFormData.start_location_pincode))
						.then((resp) => {
							const data = resp.rows[0];
							const cityState = data
								? `${data.city || ''}${data.city && data.state ? ', ' : ''}${
										data.state || ''
								  }`
								: '';
							setSourceCity(cityState);
						})
						.catch((error) => {
							console.error('Error fetching source pincode details:', error);
						});
				}

				// Fetch city/state for destination pincode
				if (restoredFormData.end_location_pincode) {
					dispatch(getPincodeList(restoredFormData.end_location_pincode))
						.then((response) => {
							const data = response.rows[0];
							const cityState = data
								? `${data.city || ''}${data.city && data.state ? ', ' : ''}${
										data.state || ''
								  }`
								: '';
							setDestCity(cityState);
						})
						.catch((error) => {
							console.error('Error fetching destination pincode details:', error);
						});
				}
			}
		}
	}, [location.state, loadDependentData, dispatch]);

	useEffect(() => {
		dispatch(
			getClientList({
				search_type: 'trip_end_reason',
				filter_by: {},
			})
		).then((res) => {
			const reason_data = res?.data?.map((item) => ({
				value: item?.id,
				label: item?.reason,
			}));
			setTripReason(reason_data);
		});
		if (permissions?.search?.includes('location') || permissions?.search?.includes('all')) {
			dispatch(
				getLocations({
					search_type: search_type?.location,
					filter_by: {},
				})
			);
		}
	}, [dispatch, id]);

	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (id) {
			setLoading(true);
			dispatch(
				getClientList({
					search_type: 'trip',
					filter_by: { id },
				})
			)
				.then((res) => {
					const trip = res?.data?.[0] || {};
					// Map API response to form fields expected by IntercityTripForm
					const info = trip.info || {};
					// Simple function to convert UTC to IST (UTC+5:30)
					setIsvendorBilling(trip?.is_vendor_billed || false);
					const convertUTCToIST = (utcTime) => {
						if (!utcTime) return undefined;
						const date = new Date(utcTime);
						date.setHours(date.getHours() + 5);
						date.setMinutes(date.getMinutes() + 30);
						return date;
					};

					const tripFormData = {
						id: trip.id, // <-- ensure id is present
						trip_type: trip.trip_type || 'adhoc',
						city: trip.city || '',
						project_id: trip.project_id || info.project_id || '',
						vendor_id: trip.vendor_id || info.vendor_id || '',
						vendor_type: trip.vendor_type || info.vendor_type || '',
						project_location_code:
							trip.project_location_code || info.project_location_code || '',
						vendor_name: trip.vendor_name || info.vendor_name || '',
						vehicle_number: trip.vehicle_number || '',
						reporting_date: convertUTCToIST(trip.reporting_time),
						start_date: convertUTCToIST(trip.start_time),
						start_location_pincode: trip.source_pincode || '',
						end_location_pincode: trip.destination_pincode || '',
						vehicle_type: trip.vehicle_type || '',
						driver_name: info.driver_name || trip.driver_name || '',
						driver_number: info.driver_number || trip.driver_number || '',
						requirement_proof: info.requirement_proof || trip.requirement_proof || [],
						client_rate: info.client_rate || trip.client_trip_cost || '',
						vendor_rate: info.vendor_rate || trip.vendor_trip_cost || '',
						vendor_rate_basis: trip.vendor_rate_basis || info.vendor_rate_basis || '',
						billing_type: trip.billing_type || info.billing_type || '',
						billing_subtype: trip.billing_subtype || info.billing_subtype || '',
						current_vendor_rate: trip.current_vendor_rate,
						current_charges_per_km: trip.current_charges_per_km,
						order_rates: trip.order_rates || info.order_rates || [],
						trip_status_id: trip.trip_status_id,
						isEnded: !!trip.end_time,
					};

					// If we have a project_id, fetch that specific project to get its name
					if (tripFormData.project_id) {
						dispatch(
							getClientList({
								search_type: 'project',
								filter_by: {
									id: tripFormData.project_id,
								},
							})
						).then((projectRes) => {
							const projectItem = projectRes?.data?.[0];
							if (projectItem) {
								setProjects([
									{
										value: projectItem.id,
										label: capitalize(projectItem.project_location_code),
									},
								]);
							}
						});
					}

					// Load dependent data first, then set form data
					loadDependentData(tripFormData).then(() => {
						setFormData(tripFormData);
					});

					// Fetch city and state information for existing pincodes in edit mode
					if (isEdit && tripFormData.start_location_pincode) {
						dispatch(getPincodeList(tripFormData.start_location_pincode)).then(
							(resp) => {
								const data = resp.rows[0];
								const cityState = data
									? `${data.city || ''}${data.city && data.state ? ', ' : ''}${
											data.state || ''
									  }`
									: '';
								setSourceCity(cityState);
							}
						);
					}

					if (isEdit && tripFormData.end_location_pincode) {
						dispatch(getPincodeList(tripFormData.end_location_pincode)).then(
							(response) => {
								const data = response.rows[0];
								const cityState = data
									? `${data.city || ''}${data.city && data.state ? ', ' : ''}${
											data.state || ''
									  }`
									: '';
								setDestCity(cityState);
							}
						);
					}

					setLoading(false);
				})
				.catch(() => setLoading(false));
		}
	}, [dispatch, id, loadDependentData]);

	function handleCitiesChange(cityValue, setFieldValue) {
		setFieldValue('project_id', undefined);
		if (!cityValue) {
			setProjects([]);
			return;
		}
		dispatch(
			getClientList({
				search_type: 'project',
				filter_by: {
					city: cityValue,
					is_active: true,
				},
			})
		).then((res) => {
			const arr = Array.isArray(res?.data) ? res?.data : Object.values(res?.data || {});
			const prodata = arr.map((item) => ({
				label: capitalize(item?.project_location_code),
				value: item?.id,
			}));
			setProjects(prodata);
		});
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	function handleProjectChange(e, setFieldValue, values) {
		if (setFieldValue) {
			setFieldValue('vehicle_type', undefined);
			setFieldValue('driver_id', undefined);
			setFieldValue('vendor_id', undefined);
		}
		// For regular trips: fetch vendor contracts and vehicle types
		if (
			values.trip_type === 'regular' &&
			(permissions?.search?.includes('vendor_contract') ||
				permissions?.search?.includes('all'))
		) {
			dispatch(
				getClientList({
					search_type: 'vendor_contract',
					filter_by: {
						project_id: e,
						city: values?.city,
						is_active: true,
					},
				})
			).then((res) => {
				const arr = Array.isArray(res?.data) ? res?.data : Object.values(res?.data || {});
				const data_vendor = arr.map((item) => ({
					value: item?.vendor_id,
					label: item?.vendor_name,
				}));
				const data_vechicleType = arr.map((item) => ({
					value: item?.vehicle_type,
					label: item?.vehicle_type,
				}));
				setVehicleTypes(getuniqueArray(data_vechicleType));
				setVendordata(getuniqueArray(data_vendor));
			});
		}
		// For adhoc trips: fetch project details and vehicle types, set adhoc flag
		if (
			values.trip_type === 'adhoc' &&
			(permissions?.search?.includes('project') || permissions?.search?.includes('all'))
		) {
			getVehicleTypes();
			getVendorDataList();
			dispatch(
				getClientList({
					search_type: 'project',
					filter_by: {
						id: e,
						is_active: true,
					},
				})
			).then((pro_res) => {
				const arr = Array.isArray(pro_res?.data)
					? pro_res?.data
					: Object.values(pro_res?.data || {});
				const proFalg = arr.map((item) => ({
					adhoc_approval_required: item?.adhoc_approval_required,
				}));
				if (proFalg && proFalg.length > 0) {
					SetProjectAdhocFlag(proFalg[0]?.adhoc_approval_required);
				}
			});
		}
	}

	function getVehicleTypes() {
		dispatch(
			getClientList({
				search_type: 'vehicle',
				filter_by: {},
			})
		).then((resVehical) => {
			const arr = Array.isArray(resVehical?.data)
				? resVehical?.data
				: Object.values(resVehical?.data || {});
			const data_vechicleType = arr.map((item) => ({
				value: item?.vehicle_type,
				label: item?.vehicle_type,
			}));
			setVehicleTypes(getuniqueArray(data_vechicleType));
		});
	}

	function getVendorDataList(type) {
		dispatch(
			getClientList({
				search_type: 'vendor',
				filter_by: {
					is_active: true,
					...(type ? { vendor_type: type } : {}),
				},
			})
		).then((res) => {
			const arr = Array.isArray(res?.data) ? res?.data : Object.values(res?.data || {});
			const data_vendor = arr.map((item) => ({
				value: item?.id,
				label: item?.vendor_info?.vendor_name,
			}));
			setVendordata(data_vendor);
		});
	}

	function handleVendorTypeChange(type, setFieldValue) {
		setFieldValue('vendor_id', undefined);
		getVendorDataList(type);
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	function handlePincodeChange(pincode, type, setFieldValue) {
		if (pincode && pincode.length === 6) {
			dispatch(getPincodeList(pincode))
				.then((res) => {
					const data = res.rows[0];
					if (data) {
						const cityState = `${data.city || ''}${
							data.city && data.state ? ', ' : ''
						}${data.state || ''}`;
						if (type === 'source') {
							setSourceCity(cityState);
							setFieldValue('start_location_city_state', cityState);
							// Auto-populate city field if not already set
							if (data.city && formRef.current) {
								const currentCity = formRef.current.values?.city;
								if (!currentCity) {
									setFieldValue('city', data.city);
									// Trigger city change to load dependent data
									handleCitiesChange(data.city, setFieldValue);
								}
							}
						} else if (type === 'destination') {
							setDestCity(cityState);
							setFieldValue('end_location_city_state', cityState);
						}
					}
				})
				.catch((error) => {
					console.error('Error fetching pincode details:', error);
					message.error('Failed to fetch city and state for pincode');
				});
		} else if (type === 'source') {
			// Clear city/state when pincode is invalid or cleared
			setSourceCity('');
			setFieldValue('start_location_city_state', '');
		} else if (type === 'destination') {
			// Clear city/state when pincode is invalid or cleared
			setDestCity('');
			setFieldValue('end_location_city_state', '');
		}
	}

	async function handleProofUpload(file, setFieldValue) {
		// Check file size (5MB limit)
		if (file.size > 5 * 1024 * 1024) {
			message.error('File size must be less than 5 MB');
			return false;
		}

		setUploading(true);
		setUploadError('');
		try {
			const res = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: { entity: 'trip', keys: [file.name] },
				})
			);
			const fileData = res?.data?.[0];
			if (!fileData?.url || !fileData?.filename) throw new Error('Failed to get signed URL');
			const signedUrl = fileData.url;
			const s3Filename = fileData.filename;
			await fetch(signedUrl, {
				method: 'PUT',
				body: file,
				headers: { 'Content-Type': file.type },
			});
			setFieldValue('requirement_proof', [s3Filename]);
			message.success('File uploaded!');
		} catch (err) {
			setUploadError('Upload failed');
			message.error('Upload failed');
		} finally {
			setUploading(false);
		}
		return false;
	}

	const handleSubmit = async (values, { setSubmitting }) => {
		const trip_info = {
			client_rate: Number(values.client_rate),
			vendor_rate: Number(values.vendor_rate),
			is_inter_city: true,
			driver_name: values.driver_name || '',
			driver_number: values.driver_number || '',
			requirement_proof:
				Array.isArray(values.requirement_proof) && values.requirement_proof.length > 0
					? values.requirement_proof
					: [''],
		};

		const dateFormatter =
			getUIVersion() === 'new' ? getServerFormatedDateV2 : getServerFormatedDate;

		const { reporting_date_payload, reporting_time_payload } = dateFormatter(
			values.reporting_date,
			'reporting_date'
		);
		const { start_date_payload, start_time_payload } = dateFormatter(
			values.start_date,
			'start_date'
		);

		const payload = {
			trip_type: values.trip_type,
			city: values.city,
			project_id: values.project_id,
			vendor_id: values.vendor_id,
			vehicle_type: values.vehicle_type,
			vehicle_number: values.vehicle_number,
			reporting_date: reporting_date_payload,
			reporting_time: `${reporting_date_payload} ${reporting_time_payload}`,
			start_date: start_date_payload,
			start_time: `${start_date_payload} ${start_time_payload}`,
			destination_pincode: values.end_location_pincode,
			source_pincode: values.start_location_pincode,
			info: trip_info,
			...(isEdit ? { id } : {}),
			...(pendingValues?.action === 'yes' ? { create_lr: true } : {}),
			adhoc_request_id: values.adhoc_request_id || '',
		};
		setModalLoading(true);
		dispatch(
			addUser({
				data: {
					data: payload,
					...(isEdit ? { update_type: 'trip' } : { create_type: 'trip' }),
				},
				setErrors: () => {},
				isEditMode: isEdit,
			})
		)
			.then((res) => {
				setSubmitting(false);
				setModalLoading(false);
				const newTripId = res?.data?.id || res?.id || res?.trip_id;
				if (isEdit) {
					message.success('Trip edited successfully');
					navigate('/trips/intercity');
				} else if (pendingValues?.action === 'yes' && newTripId) {
					navigate(`/lrmanager/create/${newTripId}`);
				} else {
					// Show success message for new trip creation without LR
					message.success('Trip created successfully without LR');
					navigate('/trips/intercity', { state: { refresh: true } });
				}
			})
			.catch(() => {
				setSubmitting(false);
				setModalLoading(false);
			});
	};

	if (isEdit && (loading || !formData?.id)) {
		return <div>Loading...</div>;
	}

	return (
		<IntercityTripForm
			isEditMode={isEdit}
			formData={formData}
			cities={cities}
			trip_reason={trip_reason}
			projectData={projectData}
			vendorsData={vendorsData}
			vehicleTypes={VehicleTypes}
			projectAdhocFlag={ProjectAdhocFlag}
			sourceCity={sourceCity}
			destCity={destCity}
			uploading={uploading}
			uploadError={uploadError}
			vendorUserType={vendorUserType}
			showLRPrompt={showLRPrompt}
			setShowLRPrompt={setShowLRPrompt}
			pendingValues={pendingValues}
			setPendingValues={setPendingValues}
			modalLoading={modalLoading}
			formRef={formRef}
			onCityChange={handleCitiesChange}
			onProjectChange={handleProjectChange}
			onVendorTypeChange={handleVendorTypeChange}
			onPincodeChange={handlePincodeChange}
			onProofUpload={handleProofUpload}
			onSubmit={handleSubmit}
			navigate={navigate}
			isvendorBilling={isvendorBilling}
		/>
	);
}
